import { Component, computed, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronsDown, lucideChevronsUp } from '@ng-icons/lucide';
import { cva } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

import { KnxBadge } from '@optical/ui/badge';
import { knx } from '@optical/ui/utils';

type ChangeDirection = 'positive' | 'negative' | 'neutral';

const changeIndicatorVariants = cva('', {
  variants: {
    direction: {
      positive: 'text-green-500',
      negative: 'text-destructive',
      neutral: 'text-muted-foreground',
    },
  },
  defaultVariants: {
    direction: 'neutral',
  },
});

@Component({
  selector: 'knx-change-indicator',
  imports: [KnxBadge, NgIcon],
  providers: [provideIcons({ lucideChevronsDown, lucideChevronsUp })],
  templateUrl: './change-indicator.html',
})
export class ChangeIndicator {
  change = input<number | null>(0, { alias: 'knxChange' });

  displayValue = computed(() => {
    const change = this.change();

    if (change === null) return '-';

    const value = change * 100;

    if (value > 999) return '999+ %';
    if (value < -999) return '-999+ %';

    const factor = Math.pow(10, 1); // Round to 1 decimal place
    const rounded = Math.round(value * factor) / factor;

    return `${rounded} %`;
  });

  positive = computed(() => {
    const change = this.change();

    return change !== null && change > 0;
  });
  negative = computed(() => {
    const change = this.change();

    return change !== null && change < 0;
  });
  neutral = computed(() => {
    const change = this.change();

    return change === null && change === 0;
  });

  protected readonly _direction = computed<ChangeDirection>(() => {
    if (this.positive()) return 'positive';
    if (this.negative()) return 'negative';

    return 'neutral';
  });

  readonly userClass = input<ClassValue>('', { alias: 'class' });

  protected readonly _computedClass = computed(() =>
    knx(changeIndicatorVariants({ direction: this._direction() }), this.userClass()),
  );
}
